import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Box,
  Typography,
  Tooltip,
  useMediaQuery,
  useTheme,
  Collapse,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  TableChart as TableIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  Assessment as AssessmentIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useAuth } from '../Auth/AuthContext';

const drawerWidth = 320;
const collapsedWidth = 64;

const Sidebar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  
  const [open, setOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});

  // Восстанавливаем состояние из localStorage
  useEffect(() => {
    const savedState = localStorage.getItem('sidebar_open');
    if (savedState !== null) {
      setOpen(JSON.parse(savedState));
    }
  }, []);

  // Сохраняем состояние в localStorage
  useEffect(() => {
    localStorage.setItem('sidebar_open', JSON.stringify(open));
  }, [open]);

  const menuItems = [
    {
      text: 'Сводка',
      icon: <DashboardIcon />,
      path: '/dashboard',
      description: 'Общая статистика',
    },
    {
      text: 'Таблица сырых данных',
      icon: <TableIcon />,
      path: '/table',
      description: 'Детальные данные',
    },
  ];

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setOpen(!open);
      // Сбрасываем hover при закрытии
      if (open) {
        setHovered(false);
      }
    }
  };

  const handleMouseEnter = () => {
    if (!open && !isMobile) {
      setHovered(true);
    }
  };

  const handleMouseLeave = () => {
    if (!open && !isMobile) {
      setHovered(false);
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    window.location.reload();
    navigate('/dashboard');
  };

  const toggleItemExpand = (text) => {
    setExpandedItems(prev => ({
      ...prev,
      [text]: !prev[text]
    }));
  };

  const drawerContent = (
    <>
      {/* Шапка сайдбара */}
      <Box 
        sx={{ 
          p: 2, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: open || hovered ? 'space-between' : 'center',
          bgcolor: 'primary.main',
          color: 'white',
          minHeight: 64,
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          opacity: open || hovered ? 1 : 0,
          transition: 'opacity 0.2s',
        }}>
          <AssessmentIcon />
          <Typography variant="h6" noWrap>
            Fleet Ratings
          </Typography>
        </Box>
        
        {!isMobile && (open || hovered) && (
          <IconButton 
            onClick={handleDrawerToggle} 
            sx={{ color: 'white' }}
            size="small"
          >
            {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        )}
      </Box>

      <Divider />

      {/* Информация о пользователе */}
      {(open || hovered) && !isMobile && (
        <>
          <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" fontWeight="bold" color="primary" noWrap>
              {user?.full_name || user?.email}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {user?.email}
            </Typography>
          </Box>
          <Divider />
        </>
      )}

      {/* Основное меню */}
      <List sx={{ flexGrow: 2, px: open || hovered ? 1 : 0.5 }}>
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path;
          
          return (
            <Tooltip 
              key={item.text}
              title={!open && !hovered ? item.text : ''}
              placement="right"
              arrow
            >
              <ListItem
                button
                onClick={() => handleNavigation(item.path)}
                sx={{
                  mb: 0.5,
                  borderRadius: 1,
                  bgcolor: isActive ? 'action.selected' : 'transparent',
                  '&:hover': {
                    bgcolor: isActive ? 'action.selected' : 'action.hover',
                  },
                  justifyContent: open || hovered ? 'initial' : 'center',
                  px: open || hovered ? 2 : 1,
                  py: 1.5,
                  minHeight: 48,
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    color: isActive ? 'primary.main' : 'action.active',
                    minWidth: 0,
                    mr: open || hovered ? 2 : 0,
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                
                <Collapse 
                  in={open || hovered} 
                  orientation="horizontal"
                  collapsedSize={0}
                >
                  <Box sx={{ 
                    width: open || hovered ? 'auto' : 0,
                    overflow: 'hidden',
                  }}>
                    <ListItemText 
                      primary={item.text} 
                      primaryTypographyProps={{
                        fontWeight: isActive ? 600 : 400,
                        color: isActive ? 'primary.main' : 'text.primary',
                        noWrap: true,
                      }}
                    />
                    <Typography 
                      variant="caption" 
                      color="text.secondary" 
                      sx={{ 
                        display: 'block',
                        lineHeight: 1,
                        mt: 0.5,
                      }}
                    >
                      {item.description}
                    </Typography>
                  </Box>
                </Collapse>
              </ListItem>
            </Tooltip>
          );
        })}
      </List>

      <Divider />

      {/* Кнопка выхода */}
      <List sx={{ px: open || hovered ? 1 : 0.5 }}>
        <Tooltip 
          title={!open && !hovered ? "Выйти" : ''}
          placement="right"
          arrow
        >
          <ListItem
            button
            onClick={handleLogout}
            sx={{
              mx: open || hovered ? 1 : 0,
              borderRadius: 1,
              '&:hover': {
                bgcolor: 'error.light',
                color: 'error.contrastText',
              },
              justifyContent: open || hovered ? 'initial' : 'center',
              px: open || hovered ? 2 : 1,
              py: 1.5,
              minHeight: 48,
            }}
          >
            <ListItemIcon 
              sx={{ 
                minWidth: 0,
                mr: open || hovered ? 2 : 0,
                justifyContent: 'center',
                color: 'inherit',
              }}
            >
              <LogoutIcon />
            </ListItemIcon>
            
            <Collapse 
              in={open || hovered} 
              orientation="horizontal"
              collapsedSize={0}
            >
              <Box sx={{ 
                width: open || hovered ? 'auto' : 0,
                overflow: 'hidden',
              }}>
                <ListItemText 
                  primary="Выйти" 
                  primaryTypographyProps={{ 
                    fontWeight: 500,
                    color: 'inherit',
                    noWrap: true,
                  }}
                />
              </Box>
            </Collapse>
          </ListItem>
        </Tooltip>
      </List>
    </>
  );

  return (
    <>
      {/* Кнопка меню для мобильных */}
      {isMobile && (
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={() => setMobileOpen(true)}
          sx={{
            position: 'fixed',
            top: 16,
            left: 16,
            zIndex: theme.zIndex.drawer + 1,
            bgcolor: 'primary.main',
            color: 'white',
            '&:hover': {
              bgcolor: 'primary.dark',
            },
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* Боковое меню для десктопа */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          anchor="left"
          open={open || hovered}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          sx={{
            width: open || hovered ? drawerWidth : collapsedWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: open || hovered ? drawerWidth : collapsedWidth,
              boxSizing: 'border-box',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              overflowX: 'hidden',
              whiteSpace: 'nowrap',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Мобильное меню */}
      {isMobile && (
        <Drawer
          variant="temporary"
          anchor="left"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </>
  );
};

export default Sidebar;
